import type { components as GiftComponents } from '@lifeserver/models/gift';

import { roundToClosestMidnightUtc } from '../utils/dates';

type PublicationWindow = GiftComponents['schemas']['GiftPublicationWindowDTO'];

/**
 * Describes what to do with a single publication window derived from a legacy
 * gift, relative to the gift's existing publications.
 */
export type PublicationAction =
    | { type: 'skip' }
    | {
          type: 'update-window';
          publicationId: string;
          startDate: string;
          endDate: string;
      }
    | { type: 'add'; startDate: string; endDate: string };

/** Compares two date strings by UTC calendar day, ignoring the time portion. */
const sameDay = (a: string | null | undefined, b: string): boolean => {
    if (!a) return false;
    return (
        new Date(a).toISOString().slice(0, 10) ===
        new Date(b).toISOString().slice(0, 10)
    );
};

/** Returns true if the date is in the future relative to `now`. */
const isFuture = (date: string, now: Date): boolean =>
    new Date(date).getTime() > now.getTime();

/**
 * Determines the publication action for a legacy gift import.
 */
export function computePublicationAction(
    publications: readonly PublicationWindow[],
    legacyStartDate: string,
    legacyEndDate: string,
    now: Date = new Date(),
): PublicationAction {
    if (publications.length === 0) {
        return {
            type: 'add',
            startDate: legacyStartDate,
            endDate: legacyEndDate,
        };
    }

    const match = publications.find((p) =>
        sameDay(
            roundToClosestMidnightUtc(p.startDate),
            roundToClosestMidnightUtc(legacyStartDate),
        ),
    );

    if (match) {
        const startMatches = sameDay(match.startDate, legacyStartDate);
        const endMatches = sameDay(match.endDate, legacyEndDate);

        if (!startMatches || !endMatches) {
            // This will "fix" potential time zones issues and normalize to midnight UTC
            return {
                type: 'update-window',
                publicationId: match.id,
                startDate: legacyStartDate,
                endDate: legacyEndDate,
            };
        } else {
            return { type: 'skip' };
        }
    }

    // No match by start date.
    const futurePublications = publications.filter((p) =>
        isFuture(p.startDate, now),
    );

    if (futurePublications.length === 1) {
        // Single future publication with different start → it moved.
        return {
            type: 'update-window',
            publicationId: futurePublications[0].id,
            startDate: legacyStartDate,
            endDate: legacyEndDate,
        };
    }

    // No (or ambiguous) future publications.
    if (isFuture(legacyStartDate, now) && isFuture(legacyEndDate, now)) {
        // Entirely in the future → new republish.
        return {
            type: 'add',
            startDate: legacyStartDate,
            endDate: legacyEndDate,
        };
    }

    // Started/past → accidental change → ignore.
    return { type: 'skip' };
}
